import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import {
  Card,
  Steps,
  Button,
  Form,
  Input,
  Select,
  Row,
  Col,
  Radio,
  InputNumber,
  Typography,
  Table,
  Space,
  Divider,
  Tag,
  Alert,
  Descriptions,
  message,
  Collapse,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  ArrowLeftOutlined,
  CopyOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { getSingleRate } from "../../redux/actions/product/addProductAction";
import { loadAllProductSubCategory } from "../../redux/actions/productSubCategory/getProductSubCategory";
import LayerSelectorBasic from "../Card/LayerSelectorBasic";
import LayerSelectorAdvance from "../Card/LayerSelectorAdvance";
import LayerTemplateSelector from "../Card/LayerTemplateSelector";
import PrintSelector from "../Card/PrintSelector";
import CoatingSelector from "../Card/CoatingSelector";
import PageTitle from "../page-header/PageHeader";
import checkTokenExp from "../../utils/checkTokenExp";
import data from "../../data/rawMaterialData";
import { buildLayersForPly } from "../../utils/layerUtils";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const layerObj = {
  layerId: 0,
  layerType: "",
  materialType: "",
  gsm: "",
  bf: "",
  ff: "",
};

const BatchRateCalculator = () => {
  const dispatch = useDispatch();
  const { unitType, ply: plyOptions, castingType } = data;

  const subCategory = useSelector(
    (state) => state.productSubCategories?.list
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [commonForm] = Form.useForm();
  const [boxForm] = Form.useForm();

  // Common data state
  const [commonData, setCommonData] = useState(null);

  // Layer state (shared)
  const [layers, setLayers] = useState([]);
  const [layerSelector, setLayerSelector] = useState(false);
  const [gsm, setGsm] = useState([]);
  const [bf, setBf] = useState([]);

  // Printing & coating (shared)
  const [isPrinting, setIsPrinting] = useState(false);
  const [isCoating, setIsCoating] = useState(false);
  const [printing, setPrinting] = useState({});
  const [coating, setCoating] = useState({ coatings: [] });

  // Box entries & results
  const [boxes, setBoxes] = useState([]);
  const [calculating, setCalculating] = useState(false);

  // Load subcategories
  useEffect(() => {
    dispatch(loadAllProductSubCategory({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Auth check
  const isLogged = Boolean(localStorage.getItem("isLogged"));
  if (!isLogged) {
    return <Navigate to={"/auth/login"} replace={true} />;
  }
  const accessToken = localStorage.getItem("access-token");
  checkTokenExp(accessToken);

  // Ply change handler – preserves existing layer values
  const handlePlyChange = (plyVal) => {
    const { layersArray, gsmArray, bfArray } = buildLayersForPly(plyVal, layers, layerObj);
    setLayers(layersArray);
    setGsm(gsmArray);
    setBf(bfArray);
  };

  // Step 1 → Step 2
  const handleCommonDataSubmit = async () => {
    try {
      const values = await commonForm.validateFields();
      setCommonData({
        ...values,
        layers,
        layerSelector,
        isPrinting,
        isCoating,
        printing,
        coating,
      });
      setCurrentStep(1);
    } catch (error) {
      if (error.errorFields) {
        toast.error("Please fill in all required fields");
      }
    }
  };

  // Add a new box entry row
  const handleAddBox = async () => {
    try {
      const values = await boxForm.validateFields();
      const subcat = subCategory?.filter(
        (item) => item.name === values.subcategory
      )[0];

      setBoxes([
        ...boxes,
        {
          key: Date.now(),
          subcategory: values.subcategory,
          length: Number(values.length),
          width: Number(values.width),
          height: Number(values.height),
          quantity: Number(values.quantity),
          numberOfPieces: subcat?.sheet_size_formula || 1,
          result: null,
          error: null,
          calculating: false,
        },
      ]);
      boxForm.resetFields();
    } catch (error) {
      if (error.errorFields) {
        toast.error("Please fill all box details");
      }
    }
  };

  // Remove a box
  const handleRemoveBox = (key) => {
    setBoxes(boxes.filter((b) => b.key !== key));
  };

  // Calculate all rates
  const handleCalculateAll = async () => {
    if (boxes.length === 0) {
      toast.error("Add at least one box to calculate");
      return;
    }

    setCalculating(true);

    const updatedBoxes = [...boxes];

    for (let i = 0; i < updatedBoxes.length; i++) {
      const box = updatedBoxes[i];

      // Skip already calculated
      if (box.result) continue;

      updatedBoxes[i] = { ...box, calculating: true };
      setBoxes([...updatedBoxes]);

      try {
        const formData = {
          subcategory: box.subcategory,
          length: box.length,
          width: box.width,
          height: box.height,
          unitType: commonData.unitType,
          casting: commonData.casting,
          quantity: box.quantity,
          ply: commonData.ply,
          layerSelector: commonData.layerSelector,
          layers: commonData.layers,
          isPrinting: commonData.isPrinting,
          printing: commonData.printing,
          isCoating: commonData.isCoating,
          coating: commonData.coating,
          topConversionRate: commonData.topConversionRate,
          boxConversionRate: commonData.boxConversionRate,
          numberOfPieces: box.numberOfPieces,
        };

        const resp = await dispatch(getSingleRate(formData));

        if (resp.message === "success") {
          updatedBoxes[i] = {
            ...box,
            calculating: false,
            result: resp.data,
            error: null,
          };
        } else {
          updatedBoxes[i] = {
            ...box,
            calculating: false,
            result: null,
            error: "Calculation failed",
          };
        }
      } catch (err) {
        updatedBoxes[i] = {
          ...box,
          calculating: false,
          result: null,
          error: err.message || "Calculation error",
        };
      }

      setBoxes([...updatedBoxes]);
    }

    setCalculating(false);
    message.success("Batch calculation complete");
  };

  // Recalculate a single box
  const handleRecalculate = async (key) => {
    const idx = boxes.findIndex((b) => b.key === key);
    if (idx === -1) return;

    const box = boxes[idx];
    const updatedBoxes = [...boxes];
    updatedBoxes[idx] = { ...box, calculating: true, result: null, error: null };
    setBoxes(updatedBoxes);

    try {
      const formData = {
        subcategory: box.subcategory,
        length: box.length,
        width: box.width,
        height: box.height,
        unitType: commonData.unitType,
        casting: commonData.casting,
        quantity: box.quantity,
        ply: commonData.ply,
        layerSelector: commonData.layerSelector,
        layers: commonData.layers,
        isPrinting: commonData.isPrinting,
        printing: commonData.printing,
        isCoating: commonData.isCoating,
        coating: commonData.coating,
        topConversionRate: commonData.topConversionRate,
        boxConversionRate: commonData.boxConversionRate,
        numberOfPieces: box.numberOfPieces,
      };

      const resp = await dispatch(getSingleRate(formData));

      if (resp.message === "success") {
        updatedBoxes[idx] = {
          ...box,
          calculating: false,
          result: resp.data,
          error: null,
        };
      } else {
        updatedBoxes[idx] = {
          ...box,
          calculating: false,
          result: null,
          error: "Calculation failed",
        };
      }
    } catch (err) {
      updatedBoxes[idx] = {
        ...box,
        calculating: false,
        result: null,
        error: err.message,
      };
    }

    setBoxes([...updatedBoxes]);
  };

  // Copy results to clipboard
  const handleCopyResults = () => {
    const calculated = boxes.filter((b) => b.result);
    if (calculated.length === 0) {
      toast.error("No results to copy");
      return;
    }

    const text = calculated
      .map(
        (b) =>
          `${b.subcategory} | ${b.length}×${b.width}×${b.height} | Qty: ${b.quantity} | Rate: ₹${Number(b.result.finalRate || 0).toFixed(2)} | RS: ${b.result.RS}" CS: ${b.result.CS}" | Wt: ${Number(b.result.boxWeight || 0).toFixed(3)}Kg`
      )
      .join("\n");

    navigator.clipboard.writeText(text);
    message.success("Results copied to clipboard");
  };

  // Results table columns
  const resultColumns = [
    {
      title: "#",
      key: "index",
      width: 40,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Subcategory",
      dataIndex: "subcategory",
      key: "subcategory",
    },
    {
      title: "Dimensions",
      key: "dimensions",
      render: (_, r) =>
        `${r.length} × ${r.width} × ${r.height}`,
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "quantity",
      align: "right",
    },
    {
      title: "Role",
      dataIndex: "packing_role",
      key: "packing_role",
      width: 80,
      render: (role) =>
        role ? (
          <Tag color={role === "inner" ? "blue" : "green"}>
            {role === "inner" ? "Inner" : "Outer"}
          </Tag>
        ) : "-",
    },
    {
      title: "Roll Size",
      key: "rs",
      align: "right",
      render: (_, r) =>
        r.result ? `${r.result.RS}"` : "-",
    },
    {
      title: "Cut Size",
      key: "cs",
      align: "right",
      render: (_, r) =>
        r.result ? `${r.result.CS}"` : "-",
    },
    {
      title: "Box Wt",
      key: "weight",
      align: "right",
      render: (_, r) =>
        r.result
          ? `${Number(r.result.boxWeight || 0).toFixed(3)} Kg`
          : "-",
    },
    {
      title: "Rate / Box",
      key: "rate",
      align: "right",
      render: (_, r) => {
        if (r.calculating) return <Tag color="processing">Calculating...</Tag>;
        if (r.error) return <Tag color="error">{r.error}</Tag>;
        if (r.result)
          return (
            <Text strong style={{ color: "#52c41a", fontSize: 14 }}>
              ₹{Number(r.result.finalRate || 0).toFixed(2)}
            </Text>
          );
        return <Tag>Pending</Tag>;
      },
    },
    {
      title: "Total",
      key: "total",
      align: "right",
      render: (_, r) =>
        r.result
          ? `₹${(
              r.quantity * (r.result.finalRate || 0)
            ).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`
          : "-",
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (_, r) => (
        <Space>
          <Button
            size="small"
            type="text"
            icon={<CalculatorOutlined />}
            onClick={() => handleRecalculate(r.key)}
            loading={r.calculating}
            title="Recalculate"
          />
          <Button
            size="small"
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveBox(r.key)}
          />
        </Space>
      ),
    },
  ];

  // Expanded row for rate breakdown
  const expandedRowRender = (record) => {
    if (!record.result) return null;
    const r = record.result;
    return (
      <Descriptions size="small" bordered column={4}>
        <Descriptions.Item label="Top Rate">
          ₹{Number(r.topRate || 0).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Rest Rate">
          ₹{Number(r.restRate || 0).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Print Cost">
          ₹{Number(r.printRate || 0).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Fixed Cost">
          ₹{Number(r.fixedCostPerBox || 0).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Lamination">
          ₹{Number(r.laminationRate || 0).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Leafing">
          ₹{Number(r.leafingRate || 0).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Spot UV">
          ₹{Number(r.spotUvRate || 0).toFixed(2)}
        </Descriptions.Item>
        <Descriptions.Item label="Net Rate">
          <Text strong>₹{Number(r.finalRate || 0).toFixed(2)}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Sheets/Box">{r.N}</Descriptions.Item>
        <Descriptions.Item label="Boxes/Sheet">{r.M}</Descriptions.Item>
        <Descriptions.Item label="Net GSM">{r.NET_GSM}</Descriptions.Item>
        <Descriptions.Item label="Top GSM">{r.TOP_GSM}</Descriptions.Item>
      </Descriptions>
    );
  };

  // Grand total
  const grandTotal = boxes.reduce(
    (sum, b) =>
      sum + (b.result ? b.quantity * (b.result.finalRate || 0) : 0),
    0
  );

  return (
    <>
      <PageTitle title="Back" />
      <Row className="mr-top" justify="center" gutter={[0, 30]}>
        <Col xs={24} xl={24} className="rounded column-design">
          <Card bordered={false}>
            <Title level={4} className="m-2 text-center">
              Batch Rate Calculator
            </Title>
            <Text
              type="secondary"
              style={{ display: "block", textAlign: "center", marginBottom: 24 }}
            >
              Enter common specs once, then calculate rates for multiple box
              sizes
            </Text>

            <Steps
              current={currentStep}
              style={{ marginBottom: 24, maxWidth: 600, margin: "0 auto 24px" }}
              items={[
                { title: "Common Data", description: "Shared specs" },
                { title: "Box Sizes", description: "Add & calculate" },
              ]}
            />

            {/* ============ STEP 0: COMMON DATA ============ */}
            {currentStep === 0 && (
              <Form
                form={commonForm}
                name="batch_common"
                labelCol={{ span: 8 }}
                labelWrap
                wrapperCol={{ span: 16 }}
                initialValues={{
                  layerSelector: "basic",
                  print_selector: "no",
                  isCoating: "no",
                }}
                autoComplete="off"
              >
                <Row gutter={16}>
                  {/* Left — Material */}
                  <Col xs={24} lg={10}>
                    <Card bordered={false}>
                      <Title level={5} className="m-2 text-center">
                        Material & Rates
                      </Title>

                      <Form.Item
                        style={{ marginBottom: 15 }}
                        name="unitType"
                        label="Unit"
                        rules={[
                          {
                            required: true,
                            message: "Please select unit type!",
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          placeholder="Unit"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.includes(input)
                          }
                        >
                          {unitType &&
                            unitType.map((unit) => (
                              <Select.Option key={unit} value={unit}>
                                {unit}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        style={{ marginBottom: 15 }}
                        name="casting"
                        label="Casting"
                        rules={[
                          {
                            required: true,
                            message: "Please select casting!",
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          placeholder="Casting"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children.includes(input)
                          }
                        >
                          {castingType &&
                            castingType.map((casting, i) => (
                              <Select.Option
                                key={i + 1}
                                value={casting.value}
                              >
                                {casting.name}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>

                      <Form.Item
                        style={{ marginBottom: 15 }}
                        label="Top Conversion Rate /Kg"
                        name="topConversionRate"
                        rules={[
                          {
                            required: true,
                            message: "Please input top conversion rate!",
                          },
                        ]}
                      >
                        <Input type="number" />
                      </Form.Item>

                      <Form.Item
                        style={{ marginBottom: 15 }}
                        label="Box Conversion Rate /Kg"
                        name="boxConversionRate"
                        rules={[
                          {
                            required: true,
                            message: "Please input box conversion rate!",
                          },
                        ]}
                      >
                        <Input type="number" />
                      </Form.Item>
                    </Card>
                  </Col>

                  {/* Right — Customization */}
                  <Col xs={24} lg={14}>
                    <Card bordered={false}>
                      <Title level={5} className="m-2 text-center">
                        Customization
                      </Title>

                      <Form.Item
                        style={{ marginBottom: 15 }}
                        name="ply"
                        label="Ply / Walls"
                        rules={[
                          {
                            required: true,
                            message: "Please select plys!",
                          },
                        ]}
                      >
                        <Select
                          showSearch
                          placeholder="Ply / Walls"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.includes(input)
                          }
                          onChange={handlePlyChange}
                        >
                          {plyOptions &&
                            plyOptions.map((p) => (
                              <Select.Option key={p.value} value={p.value}>
                                {p.name}
                              </Select.Option>
                            ))}
                        </Select>
                      </Form.Item>

                      <LayerTemplateSelector
                        ply={layers.length}
                        layers={layers}
                        setLayers={setLayers}
                        gsm={gsm}
                        setGsm={setGsm}
                        bf={bf}
                        setBf={setBf}
                      />

                      <Form.Item
                        label="Layer Selector"
                        name="layerSelector"
                        style={{ marginBottom: 15 }}
                      >
                        <Radio.Group
                          defaultValue="basic"
                          onChange={(e) =>
                            setLayerSelector(e.target.value === "advance")
                          }
                        >
                          <Radio value="basic">Basic</Radio>
                          <Radio value="advance">Advance</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {!layerSelector ? (
                        <LayerSelectorBasic
                          layers={layers}
                          setLayers={setLayers}
                          layerSelector={layerSelector}
                        />
                      ) : (
                        <LayerSelectorAdvance
                          layers={layers}
                          gsm={gsm}
                          bf={bf}
                          setLayers={setLayers}
                          setGsm={setGsm}
                          setBf={setBf}
                          layerSelector={layerSelector}
                        />
                      )}

                      <Form.Item
                        label="Printing"
                        name="print_selector"
                        style={{ marginBottom: 15 }}
                      >
                        <Radio.Group
                          defaultValue="no"
                          onChange={(e) =>
                            setIsPrinting(e.target.value === "yes")
                          }
                        >
                          <Radio value="yes">Yes</Radio>
                          <Radio value="no">No</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {isPrinting && (
                        <PrintSelector
                          printing={printing}
                          setPrinting={setPrinting}
                          isPrinting={isPrinting}
                        />
                      )}

                      <Form.Item
                        label="Coating"
                        name="isCoating"
                        style={{ marginBottom: 15 }}
                      >
                        <Radio.Group
                          defaultValue="no"
                          onChange={(e) =>
                            setIsCoating(e.target.value === "yes")
                          }
                        >
                          <Radio value="yes">Yes</Radio>
                          <Radio value="no">No</Radio>
                        </Radio.Group>
                      </Form.Item>

                      {isCoating && (
                        <CoatingSelector
                          coating={coating}
                          setCoating={setCoating}
                          isCoating={isCoating}
                        />
                      )}
                    </Card>
                  </Col>
                </Row>

                <Divider />
                <Row justify="end">
                  <Button
                    type="primary"
                    onClick={handleCommonDataSubmit}
                    shape="round"
                    size="large"
                  >
                    Next → Add Box Sizes
                  </Button>
                </Row>
              </Form>
            )}

            {/* ============ STEP 1: BOX SIZES & RESULTS ============ */}
            {currentStep === 1 && commonData && (
              <div>
                {/* Common Data Summary */}
                <Collapse
                  size="small"
                  style={{ marginBottom: 16 }}
                  items={[
                    {
                      key: "common",
                      label: (
                        <span>
                          Common Specs:{" "}
                          <Tag>{commonData.ply} Ply</Tag>
                          <Tag>Unit: {commonData.unitType}</Tag>
                          <Tag>
                            Top: ₹{commonData.topConversionRate}/Kg
                          </Tag>
                          <Tag>
                            Box: ₹{commonData.boxConversionRate}/Kg
                          </Tag>
                          {commonData.isPrinting && (
                            <Tag color="blue">Printing</Tag>
                          )}
                          {commonData.isCoating && (
                            <Tag color="purple">Coating</Tag>
                          )}
                        </span>
                      ),
                      children: (
                        <Descriptions size="small" column={4}>
                          <Descriptions.Item label="Ply">
                            {commonData.ply}
                          </Descriptions.Item>
                          <Descriptions.Item label="Unit">
                            {commonData.unitType}
                          </Descriptions.Item>
                          <Descriptions.Item label="Casting">
                            {commonData.casting}
                          </Descriptions.Item>
                          <Descriptions.Item label="Layers">
                            {commonData.layers?.length || 0} layers
                          </Descriptions.Item>
                          <Descriptions.Item label="Top Conv. Rate">
                            ₹{commonData.topConversionRate}/Kg
                          </Descriptions.Item>
                          <Descriptions.Item label="Box Conv. Rate">
                            ₹{commonData.boxConversionRate}/Kg
                          </Descriptions.Item>
                          <Descriptions.Item label="Printing">
                            {commonData.isPrinting ? "Yes" : "No"}
                          </Descriptions.Item>
                          <Descriptions.Item label="Coating">
                            {commonData.isCoating ? "Yes" : "No"}
                          </Descriptions.Item>
                        </Descriptions>
                      ),
                    },
                  ]}
                />

                {/* Add Box Form */}
                <Card
                  size="small"
                  title="Add Box Size"
                  style={{ marginBottom: 16 }}
                >
                  <Form
                    form={boxForm}
                    layout="inline"
                    style={{ flexWrap: "wrap", gap: 8 }}
                  >
                    <Form.Item
                      name="subcategory"
                      rules={[{ required: true, message: "Required" }]}
                    >
                      <Select
                        showSearch
                        placeholder="Subcategory"
                        style={{ width: 180 }}
                        loading={!subCategory}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                          option.children
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {subCategory &&
                          subCategory.map((subcat) => (
                            <Select.Option
                              key={subcat.id}
                              value={subcat.name}
                            >
                              {subcat.name}
                            </Select.Option>
                          ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name="length"
                      rules={[{ required: true, message: "L" }]}
                    >
                      <InputNumber
                        placeholder="Length"
                        style={{ width: 90 }}
                        min={0}
                      />
                    </Form.Item>
                    <Form.Item
                      name="width"
                      rules={[{ required: true, message: "W" }]}
                    >
                      <InputNumber
                        placeholder="Width"
                        style={{ width: 90 }}
                        min={0}
                      />
                    </Form.Item>
                    <Form.Item
                      name="height"
                      rules={[{ required: true, message: "H" }]}
                    >
                      <InputNumber
                        placeholder="Height"
                        style={{ width: 90 }}
                        min={0}
                      />
                    </Form.Item>
                    <Form.Item
                      name="quantity"
                      rules={[{ required: true, message: "Qty" }]}
                    >
                      <InputNumber
                        placeholder="Quantity"
                        style={{ width: 100 }}
                        min={1}
                      />
                    </Form.Item>
                    <Form.Item name="packing_role">
                      <Select
                        placeholder="Role"
                        style={{ width: 120 }}
                        allowClear
                      >
                        <Select.Option value="inner">Inner</Select.Option>
                        <Select.Option value="outer">Outer</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, cur) => prev.packing_role !== cur.packing_role}
                    >
                      {({ getFieldValue }) =>
                        getFieldValue("packing_role") === "inner" ? (
                          <Form.Item name="qty_per_pack">
                            <InputNumber
                              placeholder="Qty/Pack"
                              style={{ width: 100 }}
                              min={1}
                            />
                          </Form.Item>
                        ) : null
                      }
                    </Form.Item>
                    <Form.Item>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={handleAddBox}
                      >
                        Add
                      </Button>
                    </Form.Item>
                  </Form>
                </Card>

                {/* Results Table */}
                <Card
                  size="small"
                  title={`Box Sizes (${boxes.length})`}
                  extra={
                    <Space>
                      <Button
                        icon={<CopyOutlined />}
                        onClick={handleCopyResults}
                        disabled={!boxes.some((b) => b.result)}
                      >
                        Copy Results
                      </Button>
                      <Button
                        type="primary"
                        icon={<CalculatorOutlined />}
                        onClick={handleCalculateAll}
                        loading={calculating}
                        disabled={boxes.length === 0}
                      >
                        Calculate All
                      </Button>
                    </Space>
                  }
                >
                  <Table
                    columns={resultColumns}
                    dataSource={boxes}
                    rowKey="key"
                    pagination={false}
                    size="small"
                    expandable={{
                      expandedRowRender,
                      rowExpandable: (record) => !!record.result,
                    }}
                    locale={{
                      emptyText:
                        "No boxes added. Use the form above to add box sizes.",
                    }}
                  />

                  {boxes.some((b) => b.result) && (
                    <>
                      <Divider />
                      <Row justify="end">
                        <Col>
                          <div
                            style={{
                              background: "#f6ffed",
                              padding: "12px 24px",
                              borderRadius: 8,
                              textAlign: "right",
                            }}
                          >
                            <Text type="secondary">Grand Total: </Text>
                            <Text
                              strong
                              style={{ fontSize: 20, color: "#1890ff" }}
                            >
                              ₹
                              {grandTotal.toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                              })}
                            </Text>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </Card>

                <Divider />
                <Row justify="space-between">
                  <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => setCurrentStep(0)}
                  >
                    Back to Common Data
                  </Button>
                </Row>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default BatchRateCalculator;
